import styled, { css } from "styled-components";

import { colors, radii } from "shared/ui/tokens";

type ButtonVariant = "contained" | "outlined" | "text";
type ButtonColor = "primary" | "neutral" | "danger";
type ButtonSize = "medium" | "compact" | "small";

const SIZES: Record<ButtonSize, { height: string; padding: string; font: string; icon: string }> = {
    medium: { height: "44px", padding: "0 16px", font: "0.9375rem", icon: "18px" },
    compact: { height: "40px", padding: "0 16px", font: "0.875rem", icon: "18px" },
    small: { height: "32px", padding: "0 10px", font: "0.8125rem", icon: "16px" },
};

const containedStyles = css`
    background: ${colors.primary};
    color: #fff;
    border: 0;

    &:hover:not(:disabled) {
        background: ${colors.primaryHover};
    }
`;

const outlinedStyles = (color: ButtonColor) => {
    if (color === "danger") {
        return css`
            background: ${colors.surface};
            color: ${colors.danger};
            border: 1px solid ${colors.dangerBorder};

            &:hover:not(:disabled) {
                background: ${colors.dangerHover};
            }
        `;
    }
    if (color === "primary") {
        return css`
            background: ${colors.surface};
            color: ${colors.primary};
            border: 1px solid ${colors.fieldBorder};

            &:hover:not(:disabled) {
                border-color: ${colors.primary};
            }
        `;
    }
    return css`
        background: ${colors.surface};
        color: rgba(0, 0, 0, 0.7);
        border: 1px solid ${colors.fieldBorder};

        &:hover:not(:disabled) {
            border-color: #9e9e9e;
        }
    `;
};

const textStyles = (color: ButtonColor) => {
    const textColor =
        color === "danger" ? colors.danger : color === "primary" ? colors.primary : "rgba(0,0,0,0.7)";
    // Боковые отступы нужны, чтобы две текстовые кнопки подряд не слипались
    // и чтобы у них была нормальная область нажатия.
    return css`
        background: none;
        border: 0;
        padding: 0 8px;
        height: auto;
        color: ${textColor};

        &:hover:not(:disabled) {
            text-decoration: underline;
        }
    `;
};

export const StyledButton = styled.button<{
    $variant: ButtonVariant;
    $color: ButtonColor;
    $size: ButtonSize;
    $fontWeight?: number;
    $fullWidth?: boolean;
}>`
    height: ${({ $size }) => SIZES[$size].height};
    padding: ${({ $size }) => SIZES[$size].padding};
    border-radius: ${radii.base};
    font-size: ${({ $size }) => SIZES[$size].font};
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;

    /* Иконка рядом с подписью: MUI по умолчанию рисует её в 24px, что великовато
       для кнопки высотой 44 и совсем не годится для компактной. */
    svg {
        font-size: ${({ $size }) => SIZES[$size].icon};
    }
    ${({ $variant, $color }) =>
        $variant === "contained"
            ? containedStyles
            : $variant === "outlined"
            ? outlinedStyles($color)
            : textStyles($color)}
    ${({ $fullWidth }) =>
        $fullWidth &&
        css`
            width: 100%;
        `}
    ${({ $fontWeight }) =>
        $fontWeight != null &&
        css`
            font-weight: ${$fontWeight};
        `}

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;
